import type {
  FinanceProvider,
  FinanceRate,
  FinanceType,
} from '@kanda-libs/ks-frontend-services';

import { pipe } from 'fp-ts/lib/function';

export type GroupedByFee = Record<string, FinanceRate[]>;

export type GroupedByApr = Record<string, FinanceRate[]>;

export type GroupedByAprAndFee = Record<string, GroupedByFee>;

export interface RateDetails {
  interest: number | undefined;
  duration: number | undefined;
  successFee: number | undefined;
  minCharge: number | undefined;
  fee: number;
}

export interface LoanCosts {
  monthly: number;
  financeCost: number;
  total: number;
}

export interface Subsidies {
  successFee: number;
  percentageFee: number;
  minCharge: number;
  total: number;
}

export interface EnabledDisabled {
  enabled: FinanceRate[];
  disabled: FinanceRate[];
}

export const formatToCurrency = (
  value: number,
  currency = 'GBP',
  locale = 'en-US',
  minorUnit = 2,
  truncate = '',
): string => {
  const majorUnitValue = value / 10 ** minorUnit;
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: minorUnit,
  });
  if (!truncate) return formatter.format(majorUnitValue);
  return formatter.format(majorUnitValue).split(truncate)[0];
};

export const getRateDetails = (rate: FinanceRate): RateDetails => ({
  interest: rate?.apr || undefined,
  duration: rate?.duration || undefined,
  successFee: rate?.charge || 0,
  minCharge: rate?.min_charge || 0,
  fee: rate?.fee || 0,
});

export const calculateLoanCosts = (
  rate: FinanceRate,
  jobTotal: number,
  deposit = 0,
): LoanCosts => {
  const total = jobTotal - deposit;
  const { interest, duration } = getRateDetails(rate);

  if (interest === undefined || duration === undefined)
    return {
      monthly: 0,
      financeCost: 0,
      total: 0,
    };
  if (interest === 0)
    return {
      monthly: Math.ceil(total / duration),
      financeCost: 0,
      total,
    };
  const monthlyInterest = interest / 12 / 10000;
  const compoundInterest = (1 + monthlyInterest) ** duration;
  const monthlyLoan =
    (total * (monthlyInterest * compoundInterest)) / (compoundInterest - 1);
  const cost = Math.ceil(monthlyLoan * duration);

  return {
    monthly: Math.ceil(monthlyLoan),
    financeCost: cost - total,
    total: cost,
  };
};

export const calculateLoanSubsidy = (
  rate: FinanceRate,
  jobTotal: number,
  deposit = 0,
): Subsidies => {
  const total = jobTotal - deposit;
  const { successFee, minCharge, fee } = getRateDetails(rate);
  const percentageFee = (fee / 10000) * total;
  return {
    successFee: successFee || 0,
    percentageFee,
    minCharge: minCharge || 0,
    total: Math.max(minCharge || 0, percentageFee + (successFee || 0)),
  };
};

export const checkRateEnabled = (
  rate: FinanceRate,
  total: number,
  deposit: number,
  budget?: number,
): boolean => {
  const {
    min_deposit_pct: minDepPct,
    min_deposit_value: minDepValue,
    min_job_value: minJobValue,
    max_deposit_pct: maxDepPct,
    max_deposit_value: maxDepValue,
    max_job_value: maxJobValue,
    // credit_line: creditLine,
  } = rate;
  const loan = total - deposit;
  if (minJobValue && total < minJobValue) return false;
  if (maxJobValue && total > maxJobValue) return false;
  if (minDepValue && deposit < minDepValue) return false;
  if (maxDepValue && deposit > maxDepValue) return false;
  const depPct = (total / deposit) * 100;
  if (minDepPct && depPct < minDepPct) return false;
  if (maxDepPct && depPct > maxDepPct) return false;
  // if (creditLine && loan > creditLine) return false;
  if (budget && loan > budget) return false;
  return true;
};

export const getEnabledDisabledRates = (
  rates: FinanceRate[],
  total: number,
  deposit: number,
  budget?: number,
) =>
  rates.reduce(
    (enabledDisabled: EnabledDisabled, current: FinanceRate) => {
      const enabled = checkRateEnabled(current, total, deposit, budget);
      if (enabled) {
        enabledDisabled.enabled.push(current);
        return enabledDisabled;
      }
      enabledDisabled.disabled.push(current);
      return enabledDisabled;
    },
    {
      enabled: [],
      disabled: [],
    },
  );

export const groupAprByFee = (rates: FinanceRate[]): GroupedByFee =>
  rates.reduce((final: GroupedByFee, current: FinanceRate) => {
    const fee = current?.fee ? String(current.fee) : undefined;
    // TODO: Check with Rob if this is the correct way to handle this
    // if (!fee) return final;
    const formattedFee = fee || '0';
    if (Object.keys(final).includes(formattedFee))
      return {
        ...final,
        [formattedFee]: [...final[formattedFee], current],
      };
    return {
      ...final,
      [formattedFee]: [current],
    };
  }, {});

export const groupByFee = (grouped: GroupedByApr): GroupedByAprAndFee => {
  const aprs = Object.keys(grouped);
  return aprs.reduce(
    (final: GroupedByAprAndFee, apr: string) => ({
      ...final,
      [apr]: groupAprByFee(grouped[apr]),
    }),
    {},
  );
};

export const getAprFromName = (rate: FinanceRate): string => {
  const { name } = rate;
  if (name.includes('INTEREST_FREE')) return '0';
  return name.split('_').slice(-1)[0];
};

export const groupByAprBNPL = (rates: FinanceRate[]): GroupedByApr =>
  rates.reduce((final: GroupedByApr, rate: FinanceRate) => {
    const apr =
      rate.apr || rate.apr === 0 ? String(rate.apr) : getAprFromName(rate);
    const bnpl = rate.name.includes('BUYNOW_PAYLATER');
    const key = [String(apr), bnpl ? '-bnpl' : ''].join('');
    if (Object.keys(final).includes(key)) {
      return {
        ...final,
        [key]: [...final[key], rate],
      };
    }
    return {
      ...final,
      [key]: [rate],
    };
  }, {});

export const groupByAPR = (rates: FinanceRate[]): GroupedByApr =>
  rates.reduce((final: GroupedByApr, current: FinanceRate) => {
    const rawApr = current?.apr;
    if (rawApr === undefined) return final;
    const apr = String(rawApr);
    if (Object.keys(final).includes(apr))
      return {
        ...final,
        [apr]: [...final[apr], current],
      };
    return {
      ...final,
      [apr]: [current],
    };
  }, {});

export const filterForLine = (
  rates: FinanceRate[],
  line: FinanceType,
): FinanceRate[] =>
  rates.filter((rate: FinanceRate) => rate.finance_types.includes(line));

export const formatRates = (
  rates: FinanceRate[],
  line: FinanceType,
): GroupedByAprAndFee =>
  pipe(
    rates,
    (financeRates) => filterForLine(financeRates, line),
    groupByAPR,
    groupByFee,
  );

export const formatDuration = (duration: number) => {
  if (duration === 1) return '1 month';
  if (duration % 12 !== 0) return `${duration} months`;
  if (duration === 12) return '1 year';
  return `${duration / 12} years`;
};

export const getFinanceRateFullInfoKey = (rate: FinanceRate): string =>
  [
    rate.provider,
    rate.apr,
    rate.duration,
    rate.fee,
    rate.charge,
    rate?.deferred_duration ? 'bnpl' : undefined,
  ]
    .filter((val) => val !== undefined)
    .join('-');

export const hasMatchingRateInArray = (
  rates: FinanceRate[],
  rate: FinanceRate,
): boolean =>
  rates.some(
    (current) =>
      getFinanceRateFullInfoKey(current) === getFinanceRateFullInfoKey(rate),
  );

export const filterRatesByProvider = (
  rates: FinanceRate[],
  provider: FinanceProvider,
): FinanceRate[] => rates.filter((rate) => rate.provider === provider);

export const filterForRegulatedRates = (rates: FinanceRate[]): FinanceRate[] =>
  rates.filter(
    (rate: FinanceRate) =>
      rate.apr_type !== 'INTEREST_FREE' || (rate.duration || 0) >= 12,
  );

export const filterOutHummIfOtherRegulated = (
  rates: FinanceRate[],
): FinanceRate[] => {
  const regulatedRates = filterForRegulatedRates(rates);
  const hummRates = filterRatesByProvider(regulatedRates, 'humm').filter(
    (rate: FinanceRate) => rate.apr_type !== 'INTEREST_FREE',
  );
  if (hummRates.length === regulatedRates.length) return rates;
  return regulatedRates.filter(
    (rate) => !hasMatchingRateInArray(hummRates, rate),
  );
};

export const filterOutPropensio149IfAllium149 = (
  rates: FinanceRate[],
): FinanceRate[] => {
  const allium149 = rates.filter(
    (rate) => rate.provider === 'allium' && rate.apr === 1490,
  );
  if (allium149.length === 0) return rates;
  return rates.filter((rate) => {
    if (rate.provider !== 'propensio') return true;
    if (rate.apr_type !== 'INTEREST_BEARING') return true;
    return rate.apr !== 1490;
  });
};

export const filterOut149If129 = (rates: FinanceRate[]): FinanceRate[] => {
  const rates129 = rates.filter((rate) => rate.apr === 1290);
  if (rates129.length === 0) return rates;
  return rates.filter(
    (rate) =>
      !((rate.apr || 0) === 1490 && rate.apr_type === 'INTEREST_BEARING'),
  );
};
